export type CognitoAuthOperation =
  | 'LOGIN'
  | 'SET_PASSWORD'
  | 'CHANGE_PASSWORD'
  | 'RESET_PASSWORD_START'
  | 'RESET_PASSWORD_CONFIRM'
  | 'SIGN_UP'
  | 'CONFIRM_SIGN_UP'
  | 'RESEND_SIGN_UP_CODE';

export interface CognitoAuthErrorResult {
  key?: string;
  silent?: boolean;
}

const getErrorType = (error: any): string | undefined => {
  return (
    error?.name ||
    error?.__type ||
    error?.code ||
    error?.cause?.name ||
    error?.cause?.__type ||
    error?.cause?.code
  );
};

const getErrorMessageText = (error: any): string => {
  return String(error?.message || error?.cause?.message || '');
};

export const isAlreadySignedInError = (error: unknown): boolean => {
  const message = getErrorMessageText(error).toLowerCase();

  return (
    getErrorType(error) === 'UserAlreadyAuthenticatedException' ||
    message.includes('already a signed in user') ||
    message.includes('already signed in')
  );
};

const isAlreadyConfirmedMessage = (message: string): boolean =>
  /already confirmed|current status is confirmed/i.test(message);

const isUnauthorizedSessionError = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('access token') ||
    normalized.includes('token') ||
    normalized.includes('session') ||
    normalized.includes('expired')
  );
};

export const CognitoAuthErrorUtil = {
  getErrorMessage(
    error: unknown,
    operation: CognitoAuthOperation
  ): CognitoAuthErrorResult {
    const type = getErrorType(error);
    const message = getErrorMessageText(error);

    if (!type) {
      return { key: 'ERROR.GENERAL_ERROR' };
    }

    switch (type) {
      case 'UserNotConfirmedException':
        return { key: 'DOMAIN.AUTH.ERROR.USER_NOT_CONFIRMED' };

      case 'PasswordResetRequiredException':
        return { key: 'DOMAIN.AUTH.ERROR.PASSWORD_RESET_REQUIRED' };

      case 'UsernameExistsException':
      case 'AliasExistsException':
        return { key: 'VALIDATION.EMAIL_IN_USE' };

      case 'NotAuthorizedException':
        // "SignUp is not permitted for this user pool": self sign-up is off.
        if (operation === 'SIGN_UP') {
          return { key: 'DOMAIN.AUTH.ERROR.SIGN_UP_UNAVAILABLE' };
        }
        if (
          operation === 'CONFIRM_SIGN_UP' &&
          isAlreadyConfirmedMessage(message)
        ) {
          return { key: 'DOMAIN.AUTH.ERROR.ACCOUNT_ALREADY_CONFIRMED' };
        }
        if (operation === 'CHANGE_PASSWORD') {
          if (isUnauthorizedSessionError(message)) {
            return { key: 'ERROR.UNAUTHORIZED' };
          }
          return { key: 'DOMAIN.AUTH.ERROR.INCORRECT_OLD_PASSWORD' };
        }
        // Cognito's lockout after repeated failures arrives as NotAuthorizedException too.
        if (/password attempts exceeded/i.test(message ?? '')) {
          return { key: 'ERROR.TOO_MANY_ATTEMPTS' };
        }
        return { key: 'DOMAIN.AUTH.ERROR.BAD_CREDENTIALS' };

      case 'UserNotFoundException':
        // Never reveal whether an e-mail has an account.
        if (
          operation === 'RESET_PASSWORD_START' ||
          operation === 'RESEND_SIGN_UP_CODE'
        ) {
          return { silent: true };
        }
        if (operation === 'CONFIRM_SIGN_UP') {
          return { key: 'DOMAIN.AUTH.ERROR.SIGN_UP_CODE_INVALID' };
        }
        return { key: 'DOMAIN.AUTH.ERROR.BAD_CREDENTIALS' };

      case 'InvalidPasswordException':
      case 'PasswordHistoryPolicyViolationException':
        return { key: 'DOMAIN.AUTH.ERROR.PASSWORD_POLICY' };

      case 'CodeMismatchException':
        if (operation === 'CONFIRM_SIGN_UP') {
          return { key: 'DOMAIN.AUTH.ERROR.SIGN_UP_CODE_INVALID' };
        }
        return { key: 'DOMAIN.AUTH.ERROR.RESET_PASSWORD_CODE_INVALID' };

      case 'ExpiredCodeException':
        if (operation === 'CONFIRM_SIGN_UP') {
          return { key: 'DOMAIN.AUTH.ERROR.SIGN_UP_CODE_EXPIRED' };
        }
        return { key: 'DOMAIN.AUTH.ERROR.RESET_PASSWORD_CODE_EXPIRED' };

      case 'InvalidParameterException':
        if (
          operation === 'RESEND_SIGN_UP_CODE' &&
          isAlreadyConfirmedMessage(message)
        ) {
          return { key: 'DOMAIN.AUTH.ERROR.ACCOUNT_ALREADY_CONFIRMED' };
        }
        if (
          operation === 'RESET_PASSWORD_START' &&
          message.toLowerCase().includes('verified') &&
          message.toLowerCase().includes('email')
        ) {
          return {
            key: 'DOMAIN.AUTH.ERROR.RESET_PASSWORD_NO_VERIFIED_CONTACT',
          };
        }
        return { key: 'ERROR.GENERAL_ERROR' };

      case 'LimitExceededException':
      case 'TooManyRequestsException':
      case 'TooManyFailedAttemptsException':
      case 'PasswordAttemptsExceededException':
        return { key: 'ERROR.TOO_MANY_ATTEMPTS' };

      case 'InternalErrorException':
      case 'ResourceNotFoundException':
      case 'ForbiddenException':
        return { key: 'ERROR.SERVER' };

      default:
        return { key: 'ERROR.GENERAL_ERROR' };
    }
  },
};
